import { useGlobalUserProfile } from "./useGlobalUser";
import { normalizeDate } from "../utils/dateUtils";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

/**
 * A hook that provides a date normalization function based on the user's preferred date format.
 * Useful for converting API date strings (in user-defined formats) to ISO format (YYYY-MM-DD).
 */

const mapStrftimeToDayjs = (format: string) => {
  if (!format) return format;
  return format
    .replace(/%Y/g, "YYYY")
    .replace(/%y/g, "YY")
    .replace(/%m/g, "MM")
    .replace(/%d/g, "DD")
    .replace(/%H/g, "HH")
    .replace(/%M/g, "mm")
    .replace(/%S/g, "ss")
    .replace(/%b/g, "MMM")
    .replace(/%B/g, "MMMM");
};

export const useDateNormalization = () => {
  const { data: userProfile } = useGlobalUserProfile();
  const rawDateFormat = userProfile?.utility?.tenant?.dateFormat || "DD/MM/YYYY";
  const userDateFormat = mapStrftimeToDayjs(rawDateFormat);

  /**
   * Normalizes a date string to YYYY-MM-DD.
   * Prioritizes the user's preferred format, then falls back to general normalization.
   */
  const normalizeWithUserFormat = (dateStr: any) => {
    if (!dateStr || typeof dateStr !== "string") return "";

    // Attempt to parse with user's specific format first
    const parsed = dayjs(dateStr, userDateFormat, true);
    if (parsed.isValid()) {
      return parsed.format("YYYY-MM-DD");
    }

    // Fallback to general normalization logic
    return normalizeDate(dateStr);
  };

  return { 
    normalizeWithUserFormat, 
    userDateFormat,
    // Helper to format ISO to user format (useful for display elsewhere)
    formatToUserDate: (isoDate: string) => isoDate ? dayjs(isoDate).format(userDateFormat) : ""
  };
};
